// ═══════════════════════════════════════════════════════════════════════
// esbIntegrationTab.jsx — Enterprise Service Bus design visualization
// ═══════════════════════════════════════════════════════════════════════
// Visualizes the full ESB design from CLM_ESB.pptx + CLM_ESB_Siemens.pptx:
//   · 3 pillars · 7 reference planes · 8 building blocks
//   · 3 delivery semantics + 4 ACK protocols
//   · 3 persistence layers · Siemens-native integration stack
//   · 6 tier-1 CLM flows · vendor matrix · anti-patterns
//   · NEW: Animated Producer→Broker→Consumer swim diagram with live messages
// ═══════════════════════════════════════════════════════════════════════

import React, { useState } from "react";
import {
  ESB_PILLARS, ESB_PLANES, ESB_BUILDING_BLOCKS,
  ESB_SEMANTICS, ACK_PROTOCOLS, PERSISTENCE_LAYERS,
  SIEMENS_STACK, TIER1_FLOWS, VENDOR_MATRIX, ESB_ANTI_PATTERNS,
} from "./esbData";
import { T, FONTS, chipBtn } from "./theme";
import { FLOWS, getInterfacesForFlow } from "./crossRefs";
import { EntityRefList } from "./navigation";

const SECTIONS = [
  { id: "pillars",   label: "🏛️ 3 Pillars",            color: T.teal },
  { id: "live",      label: "⚡ Live flow (animated)",   color: T.green },
  { id: "planes",    label: "🏗️ 7-plane architecture",  color: T.cyan },
  { id: "blocks",    label: "🧱 8 Building blocks",     color: T.purple },
  { id: "semantics", label: "📬 Delivery semantics",    color: T.amber },
  { id: "acks",      label: "✅ ACK protocols (4)",     color: T.green },
  { id: "persist",   label: "💾 Persistence (L1-L3)",   color: T.gold },
  { id: "siemens",   label: "🏭 Siemens stack",         color: T.orange },
  { id: "flows",     label: "🔄 Tier-1 flows (6)",      color: T.pink },
  { id: "vendors",   label: "🥇 Vendor matrix",         color: T.blue },
  { id: "anti",      label: "⚠️ Anti-patterns (8)",    color: T.red },
];

const STARS = (n) => "★".repeat(n) + "☆".repeat(5 - n);

export default function ESBIntegrationTab() {
  const [section, setSection] = useState("live");

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.teal, marginBottom: 4 }}>
          🚌 ESB for CLM · Siemens Stack · Guaranteed Delivery
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          7 systems · 1 bus · guaranteed delivery on every tier-1 flow · B2B kept off this bus by design
        </div>
        <div style={{ fontSize: 9.5, color: T.amber, marginTop: 6, fontStyle: "italic" }}>
          "If a message can be lost, it will be. Build for the guarantee, not the happy path."
        </div>
      </div>

      {/* Headline stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { l: "Tier-1 SLA",  v: "99.95%", c: T.teal },
          { l: "P99 latency", v: "<500ms", c: T.amber },
          { l: "Loss target", v: "0",      c: T.green },
          { l: "Systems",     v: "7",      c: T.cyan },
          { l: "Tier-1 flows",v: "6",      c: T.pink },
          { l: "Recommended", v: "Kafka",  c: T.gold },
        ].map(k => (
          <div key={k.l} style={{
            background: T.bg2, border: `1px solid ${k.c}30`, borderRadius: 6,
            padding: "8px 16px", textAlign: "center",
          }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 7.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase" }}>{k.l}</div>
          </div>
        ))}
      </div>

      {/* Section nav */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)} style={chipBtn(section === s.id, s.color)}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ─── NEW: LIVE FLOW ANIMATED ─── */}
      {section === "live" && <LiveFlowSection />}

      {/* PILLARS */}
      {section === "pillars" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {ESB_PILLARS.map(p => (
            <div key={p.id} style={{
              background: T.bg2, border: `1px solid ${p.color}40`,
              borderLeft: `4px solid ${p.color}`, borderRadius: 8, padding: "18px 20px",
            }}>
              <div style={{ fontFamily: FONTS.header, fontSize: 22, fontWeight: 700, color: p.color, letterSpacing: 2, marginBottom: 10 }}>
                {p.id}
              </div>
              <div style={{ fontSize: 11, color: T.text, lineHeight: 1.5, fontStyle: "italic" }}>{p.desc}</div>
            </div>
          ))}
        </div>
      )}

      {/* PLANES */}
      {section === "planes" && (
        <div>
          <div style={{ fontSize: 10, color: T.text2, marginBottom: 12, fontStyle: "italic" }}>
            Each plane has a single responsibility, an owner, a versioning strategy, and an SLO.
            Mixing concerns across planes is how ESBs become Big Balls of Mud.
          </div>
          {ESB_PLANES.map(p => (
            <div key={p.name} style={{
              display: "flex", alignItems: "stretch", marginBottom: 6,
              background: T.bg2, borderLeft: `4px solid ${p.color}`,
              border: `1px solid ${p.color}30`, borderRadius: 6, overflow: "hidden",
            }}>
              <div style={{
                background: `${p.color}10`, padding: "12px 14px",
                minWidth: 100, borderRight: `1px solid ${p.color}30`,
                fontSize: 8.5, color: p.color, fontFamily: FONTS.mono,
                fontWeight: 700, letterSpacing: 1, textAlign: "center",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {p.tag}
              </div>
              <div style={{ padding: "10px 16px", flex: 1 }}>
                <div style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color: p.color, letterSpacing: 1, marginBottom: 4 }}>
                  {p.name}
                </div>
                <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.4 }}>{p.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* BUILDING BLOCKS */}
      {section === "blocks" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {ESB_BUILDING_BLOCKS.map(b => (
            <div key={b.num} style={{
              background: T.bg2, border: `1px solid ${T.purple}30`,
              borderTop: `3px solid ${T.purple}`, borderRadius: 6, padding: "12px 14px", position: "relative",
            }}>
              <div style={{
                position: "absolute", right: 14, top: 8,
                fontFamily: FONTS.header, fontSize: 24, fontWeight: 700,
                color: T.purple, opacity: 0.4,
              }}>{b.num}</div>
              <div style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color: T.purple, letterSpacing: 1, marginBottom: 6 }}>
                {b.name}
              </div>
              <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5, marginBottom: 10 }}>{b.desc}</div>
              <div style={{ borderTop: `1px solid ${T.purple}20`, paddingTop: 8 }}>
                <div style={{ fontSize: 7.5, color: T.text3, letterSpacing: 1, textTransform: "uppercase" }}>Vendors</div>
                <div style={{ fontSize: 9, color: T.text2, fontStyle: "italic", marginTop: 3 }}>{b.vendors}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SEMANTICS */}
      {section === "semantics" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {ESB_SEMANTICS.map(s => (
            <div key={s.id} style={{
              background: T.bg2, border: `1px solid ${s.color}40`,
              borderTop: `4px solid ${s.color}`, borderRadius: 8, padding: "14px 16px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontFamily: FONTS.header, fontSize: 14, fontWeight: 700, color: s.color, letterSpacing: 2, textTransform: "uppercase" }}>
                  {s.id}
                </span>
                <span style={{
                  background: s.color, color: "#000",
                  fontFamily: FONTS.header, fontSize: 14, fontWeight: 700,
                  width: 38, height: 38, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{s.symbol}</span>
              </div>
              {[
                { l: "MEANS",    v: s.means },
                { l: "USE FOR",  v: s.useFor },
                { l: "HOW",      v: s.how },
                { l: "COST",     v: s.cost },
                { l: "EXAMPLE",  v: s.example, italic: true },
              ].map(row => (
                <div key={row.l} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${s.color}20` }}>
                  <div style={{ fontSize: 8, color: s.color, letterSpacing: 1, fontWeight: 700, marginBottom: 3 }}>{row.l}</div>
                  <div style={{ fontSize: 10, color: T.text2, fontStyle: row.italic ? "italic" : "normal" }}>{row.v}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ACK PROTOCOLS */}
      {section === "acks" && (
        <div>
          <div style={{ fontSize: 10, color: T.text2, marginBottom: 12, fontStyle: "italic" }}>
            Four ACKs. Get any one wrong and you lose messages. None of these are optional.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
            {ACK_PROTOCOLS.map(a => (
              <div key={a.num} style={{
                background: T.bg2, border: `1px solid ${a.color}40`,
                borderTop: `3px solid ${a.color}`, borderRadius: 6, padding: "12px 14px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <span style={{
                    fontFamily: FONTS.header, fontSize: 26, fontWeight: 700,
                    color: a.color, opacity: 0.6,
                  }}>{a.num}</span>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700, color: T.text, letterSpacing: 1 }}>
                    {a.chain}
                  </span>
                </div>
                {[
                  { l: "Trigger", v: a.trigger,  c: a.color },
                  { l: "Waits",   v: a.waits,    c: a.color },
                  { l: "Signal",  v: a.signal,   c: T.green },
                  { l: "Failure", v: a.failure,  c: T.amber },
                  { l: "Why",     v: a.why,      c: T.text2, italic: true },
                ].map(row => (
                  <div key={row.l} style={{ marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${a.color}15` }}>
                    <span style={{ fontSize: 7.5, color: row.c, letterSpacing: 1, textTransform: "uppercase" }}>{row.l}: </span>
                    <span style={{ fontSize: 9.5, color: row.italic ? T.text2 : T.text, fontStyle: row.italic ? "italic" : "normal" }}>{row.v}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PERSISTENCE */}
      {section === "persist" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {PERSISTENCE_LAYERS.map(l => (
              <div key={l.id} style={{
                background: T.bg2, border: `1px solid ${l.color}40`,
                borderTop: `4px solid ${l.color}`, borderRadius: 8, padding: "16px 18px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <span style={{ fontFamily: FONTS.header, fontSize: 36, fontWeight: 700, color: l.color }}>{l.id}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text, letterSpacing: 1 }}>{l.name}</span>
                </div>
                <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.5, marginBottom: 10 }}>{l.desc}</div>
                {[
                  { l: "ACK",       v: l.ack,      c: l.color },
                  { l: "DATA LOSS", v: l.dataLoss, c: l.color },
                  { l: "WHEN",      v: l.when,     c: l.color, w: true },
                ].map(row => (
                  <div key={row.l} style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${l.color}20` }}>
                    <div style={{ fontSize: 7.5, color: row.c, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>{row.l}</div>
                    <div style={{ fontSize: 9.5, color: row.w ? l.color : T.text2, marginTop: 2, fontWeight: row.w ? 700 : 400 }}>{row.v}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{
            marginTop: 14, padding: "10px 14px",
            background: T.bg2, border: `1px solid ${T.green}30`, borderRadius: 6,
          }}>
            <div style={{ fontSize: 9, color: T.green, fontWeight: 700, marginBottom: 4 }}>
              ▶ PRODUCTION ANSWER FOR CLM
            </div>
            <div style={{ fontSize: 9, color: T.text, fontFamily: "monospace" }}>
              Kafka config: <span style={{ color: T.cyan }}>acks=all</span> · <span style={{ color: T.cyan }}>min.insync.replicas=2</span> ·{" "}
              <span style={{ color: T.cyan }}>replication.factor=3</span> · <span style={{ color: T.cyan }}>unclean.leader.election=false</span> ·{" "}
              <span style={{ color: T.cyan }}>enable.idempotence=true</span> · retention=7d (default), 90d (regulated)
            </div>
          </div>
        </div>
      )}

      {/* SIEMENS STACK */}
      {section === "siemens" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 12 }}>
          {SIEMENS_STACK.map(s => (
            <div key={s.name} style={{
              background: T.bg2, border: `1px solid ${s.color}40`,
              borderTop: `3px solid ${s.color}`, borderRadius: 6, padding: "12px 14px",
            }}>
              <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700, color: s.color, letterSpacing: 1, marginBottom: 3 }}>
                {s.name}
              </div>
              <div style={{ fontSize: 8.5, color: T.text3, fontStyle: "italic", marginBottom: 10 }}>{s.product}</div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 7.5, color: s.color, fontWeight: 700, letterSpacing: 1 }}>SCOPE · </span>
                <span style={{ fontSize: 9, color: T.text, fontFamily: "monospace" }}>{s.scope}</span>
              </div>
              <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5, marginBottom: 10 }}>{s.detail}</div>
              <div style={{ borderTop: `1px solid ${s.color}20`, paddingTop: 6 }}>
                <span style={{ fontSize: 7.5, color: T.text3, letterSpacing: 1 }}>PROTOCOLS · </span>
                <span style={{ fontSize: 9, color: s.color, fontFamily: FONTS.mono }}>{s.proto}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TIER-1 FLOWS */}
      {section === "flows" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 12 }}>
          {TIER1_FLOWS.map(f => {
            const flowId = `F${parseInt(f.num, 10)}`;
            const ifaces = getInterfacesForFlow(flowId);
            return (
              <div key={f.num} style={{
                background: T.bg2, border: `1px solid ${f.color}40`,
                borderTop: `3px solid ${f.color}`, borderRadius: 6, padding: "12px 14px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: T.text2 }}>{f.num}</span>
                  <span style={{
                    fontSize: 7, padding: "1px 8px", borderRadius: 8,
                    background: `${f.color}25`, color: f.color, letterSpacing: 1, fontWeight: 700,
                  }}>{f.tier}</span>
                </div>
                <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6 }}>
                  {f.name}
                </div>
                <div style={{ fontSize: 10, color: f.color, fontFamily: FONTS.mono, marginBottom: 8 }}>
                  {f.route}
                </div>
                <div style={{ fontSize: 9.5, color: T.text2, fontStyle: "italic", lineHeight: 1.5, marginBottom: 8 }}>
                  {f.desc}
                </div>
                <div style={{ borderTop: `1px solid ${f.color}20`, paddingTop: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 7.5, color: T.green, fontWeight: 700, letterSpacing: 1 }}>SLA: </span>
                  <span style={{ fontSize: 9, color: T.green }}>{f.sla}</span>
                </div>
                {ifaces.length > 0 && (
                  <EntityRefList kind="interface" ids={ifaces} label="Interfaces" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* VENDOR MATRIX */}
      {section === "vendors" && (
        <div>
          <div style={{ overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
              <thead>
                <tr>
                  <th style={th}>Criterion</th>
                  <th style={{ ...th, color: T.teal, background: `${T.teal}10`, borderTop: `2px solid ${T.teal}` }}>KAFKA ⭐</th>
                  <th style={th}>RABBITMQ</th>
                  <th style={th}>IBM MQ</th>
                  <th style={th}>SOLACE</th>
                  <th style={th}>PULSAR</th>
                </tr>
              </thead>
              <tbody>
                {VENDOR_MATRIX.map(row => (
                  <tr key={row.criterion} style={{ borderBottom: `1px solid ${T.border2}` }}>
                    <td style={{ ...td, fontWeight: 600 }}>{row.criterion}</td>
                    <td style={{ ...td, color: T.teal, background: `${T.teal}05` }}>{STARS(row.kafka)}</td>
                    <td style={td}>{STARS(row.rabbitmq)}</td>
                    <td style={td}>{STARS(row.ibmmq)}</td>
                    <td style={td}>{STARS(row.solace)}</td>
                    <td style={td}>{STARS(row.pulsar)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{
            marginTop: 14, padding: "12px 16px",
            background: `${T.teal}10`, border: `1px solid ${T.teal}40`, borderRadius: 6,
          }}>
            <div style={{ fontSize: 11, color: T.teal, fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>
              🏆 RECOMMENDATION · KAFKA AS BROKER · CAMEL/SPRING AS ROUTER · CONFLUENT SR AS REGISTRY
            </div>
            <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5, fontStyle: "italic" }}>
              Kafka wins for CLM: highest throughput, log-based replay native, strong ordering per key, best ecosystem
              (Debezium for outbox CDC, MirrorMaker 2 for DR, Connect for adapters), best Siemens AIG fit
              (AMQP/Kafka bridge is the documented pattern). Use MSK / Confluent Cloud / Strimzi based on cloud strategy.
            </div>
          </div>
          <div style={{
            marginTop: 10, padding: "8px 14px",
            background: `${T.amber}10`, border: `1px solid ${T.amber}30`, borderRadius: 6,
            fontSize: 9, color: T.amber, fontStyle: "italic",
          }}>
            B2B vendors (Mulesoft · Boomi · IBM Sterling · Cleo) deliberately excluded — that scope is out per integrator design.
          </div>
        </div>
      )}

      {/* ANTI-PATTERNS */}
      {section === "anti" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 10 }}>
          {ESB_ANTI_PATTERNS.map((a, i) => (
            <div key={a.name} style={{
              background: T.bg2, border: `1px solid ${T.red}40`,
              borderLeft: `3px solid ${T.red}`, borderRadius: 6, padding: "10px 14px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                <span style={{ fontFamily: FONTS.header, fontSize: 11, fontWeight: 700, color: T.red }}>
                  {a.name}
                </span>
                <span style={{ color: T.red, fontSize: 14 }}>✕</span>
              </div>
              <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5 }}>{a.detail}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const th = {
  padding: "8px 12px", textAlign: "left", fontSize: 9, color: T.text2,
  fontFamily: FONTS.mono, letterSpacing: 1, textTransform: "uppercase",
  borderBottom: `1px solid ${T.border}`, background: T.bg3, fontWeight: 600,
};
const td = { padding: "8px 12px", fontSize: 10, color: T.text };

// ═══════════════════════════════════════════════════════════════════════
// LiveFlowSection — Animated Producer→Broker→Consumer swim diagram
// ═══════════════════════════════════════════════════════════════════════
// Visualizes the 7-step at-least-once delivery happy-path (slide 13 of
// CLM_ESB_Siemens.pptx). Animated dots move through:
//   1. Producer writes outbox
//   2. Producer publishes to broker
//   3. Broker persists & replicates (acks=all, min.insync.replicas=2)
//   4. Broker ACKs producer
//   5. Consumer polls partition
//   6. Consumer processes (idempotency check)
//   7. Consumer commits offset
// Plus failure-mode toggle: pause animation, freeze at step 6 to show
// the duplicate-delivery risk window.
// ═══════════════════════════════════════════════════════════════════════

function LiveFlowSection() {
  const [showFailure, setShowFailure] = useState(false);
  const [speed, setSpeed]             = useState("normal"); // slow|normal|fast
  const [selectedFlowId, setSelectedFlowId] = useState("F1");

  const dur = speed === "slow" ? 8 : speed === "fast" ? 2.4 : 4.6;
  const selectedFlow = FLOWS.find(f => f.id === selectedFlowId) || FLOWS[0];
  const ifaces = getInterfacesForFlow(selectedFlow.id);

  // Lane geometry
  const W = 1100, H = 360;
  const LANE_X = 80, LANE_W = W - 100;
  const lanes = [
    { id: "producer", label: "PRODUCER", desc: "AIG · T4S · Opcenter outbox", color: T.cyan,   y: 40 },
    { id: "broker",   label: "BROKER",   desc: "Kafka · 3 replicas · acks=all", color: T.green, y: 150 },
    { id: "consumer", label: "CONSUMER", desc: "SAP T4S · Opcenter SOA · IX",  color: T.amber,  y: 260 },
  ];

  // 7 steps — each one is { lane, x_pct, label, sub }
  // Coordinates are within the active lane band
  const steps = [
    { num: 1, lane: 0, xPct: 0.04,  label: "WRITE OUTBOX",     sub: "Same DB txn",       cuts: 0 },
    { num: 2, lane: 0, xPct: 0.18,  label: "PUBLISH",          sub: "Outbox relay",       cuts: 0 },
    { num: 3, lane: 1, xPct: 0.32,  label: "PERSIST+REPLICATE",sub: "min.insync=2",       cuts: 0 },
    { num: 4, lane: 1, xPct: 0.48,  label: "BROKER ACK",       sub: "Mark outbox sent",  cuts: 0 },
    { num: 5, lane: 2, xPct: 0.62,  label: "CONSUMER POLL",    sub: "Read batch",         cuts: 0 },
    { num: 6, lane: 2, xPct: 0.78,  label: "PROCESS+COMMIT",   sub: "Idempotency check", cuts: 1 },
    { num: 7, lane: 2, xPct: 0.92,  label: "OFFSET COMMIT",    sub: "Move forward",       cuts: 0 },
  ];

  // Compute absolute coordinates
  const stepPos = (s) => {
    const lane = lanes[s.lane];
    const x = LANE_X + s.xPct * LANE_W;
    return { x, y: lane.y + 30, lane };
  };

  return (
    <div>
      <div style={{
        padding: "10px 14px", marginBottom: 14,
        background: `${T.green}10`, border: `1px solid ${T.green}30`, borderRadius: 6,
        fontSize: 10, color: T.green,
      }}>
        ⚡ <strong>Live tier-1 flow.</strong> Watch a real message traverse producer → broker → consumer.
        Toggle the failure mode to see the duplicate-delivery window between step 6 and step 7 (the reason consumers must be idempotent).
      </div>

      {/* Flow + controls */}
      <div style={{
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
        marginBottom: 14, padding: "10px 14px",
        background: T.bg2, borderRadius: 6, border: `1px solid ${T.border}`,
      }}>
        <span style={{ fontSize: 9, color: T.text2, fontFamily: FONTS.mono, letterSpacing: 1 }}>FLOW ▸</span>
        {FLOWS.map(f => (
          <button key={f.id} onClick={() => setSelectedFlowId(f.id)} style={chipBtn(selectedFlowId === f.id, f.color)}>
            {f.id} · {f.name}
          </button>
        ))}
        <span style={{ marginLeft: 16, fontSize: 9, color: T.text2, fontFamily: FONTS.mono, letterSpacing: 1 }}>SPEED ▸</span>
        {["slow", "normal", "fast"].map(s => (
          <button key={s} onClick={() => setSpeed(s)} style={chipBtn(speed === s, T.cyan)}>{s}</button>
        ))}
        <label style={{
          marginLeft: 16, fontSize: 10, color: showFailure ? T.red : T.text2,
          display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
        }}>
          <input type="checkbox" checked={showFailure} onChange={e => setShowFailure(e.target.checked)} />
          <span>⚠️ Show failure window (between step 6 and 7)</span>
        </label>
      </div>

      {/* SVG canvas */}
      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
        overflow: "auto", padding: 12, marginBottom: 14,
      }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
          <defs>
            <marker id="esb-arr" viewBox="0 0 10 10" refX="9" refY="5"
              markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L10,5 L0,10 Z" fill={selectedFlow.color}/>
            </marker>
            <filter id="esb-glow">
              <feGaussianBlur stdDeviation="2.5" result="b"/>
              <feMerge>
                <feMergeNode in="b"/><feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Swim lanes */}
          {lanes.map((lane, i) => (
            <g key={lane.id}>
              <rect x={LANE_X} y={lane.y} width={LANE_W} height="70"
                fill={`${lane.color}05`}
                stroke={`${lane.color}30`}
                strokeWidth="1" strokeDasharray="3 3"
              />
              <text x={LANE_X - 10} y={lane.y + 22} fontSize="11"
                fontFamily={FONTS.mono} fontWeight="700"
                fill={lane.color} textAnchor="end" letterSpacing="1">
                {lane.label}
              </text>
              <text x={LANE_X - 10} y={lane.y + 40} fontSize="8"
                fill={T.text2} textAnchor="end" fontStyle="italic">
                {lane.desc}
              </text>
            </g>
          ))}

          {/* Path through all 7 steps (a single polyline through step points) */}
          {(() => {
            const pts = steps.map(stepPos);
            // Build a path with cubic-ish curves between lanes
            let d = `M ${pts[0].x} ${pts[0].y}`;
            for (let i = 1; i < pts.length; i++) {
              const prev = pts[i - 1], cur = pts[i];
              if (prev.y === cur.y) {
                d += ` L ${cur.x} ${cur.y}`;
              } else {
                const mx = (prev.x + cur.x) / 2;
                d += ` C ${mx} ${prev.y}, ${mx} ${cur.y}, ${cur.x} ${cur.y}`;
              }
            }
            return (
              <>
                <path id="esb-livepath" d={d}
                  stroke={selectedFlow.color} strokeWidth="2.5"
                  strokeOpacity="0.7" fill="none"
                  markerEnd="url(#esb-arr)"
                />
                {/* Animated message dot — pause/restart based on showFailure */}
                <circle r="6" fill={selectedFlow.color} filter="url(#esb-glow)">
                  <animateMotion
                    key={`anim-${selectedFlowId}-${speed}-${showFailure}`}
                    dur={`${dur}s`}
                    repeatCount={showFailure ? "1" : "indefinite"}
                    keyPoints={showFailure ? "0;0.83" : undefined}
                    keyTimes={showFailure ? "0;1" : undefined}
                    fill={showFailure ? "freeze" : undefined}>
                    <mpath href="#esb-livepath"/>
                  </animateMotion>
                </circle>
              </>
            );
          })()}

          {/* Step dots + labels */}
          {steps.map((s) => {
            const { x, y, lane } = stepPos(s);
            const isCutPoint = showFailure && s.cuts === 1;
            return (
              <g key={s.num}>
                <circle cx={x} cy={y} r="20"
                  fill={isCutPoint ? `${T.red}25` : `${lane.color}25`}
                  stroke={isCutPoint ? T.red : lane.color}
                  strokeWidth="2"
                />
                <text x={x} y={y + 5} fontSize="14"
                  fontFamily={FONTS.header} fontWeight="700"
                  fill={isCutPoint ? T.red : lane.color}
                  textAnchor="middle">
                  {s.num}
                </text>
                <text x={x} y={y - 30} fontSize="8.5"
                  fontFamily={FONTS.mono} fontWeight="700"
                  fill={isCutPoint ? T.red : T.text}
                  textAnchor="middle" letterSpacing="1">
                  {s.label}
                </text>
                <text x={x} y={y + 35} fontSize="8"
                  fill={T.text2} textAnchor="middle" fontStyle="italic">
                  {s.sub}
                </text>
                {isCutPoint && (
                  <text x={x} y={y + 50} fontSize="8" fill={T.red} textAnchor="middle"
                    fontFamily={FONTS.mono} fontWeight="700">
                    ✂ FAILURE WINDOW
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Failure-mode explanation */}
      {showFailure && (
        <div style={{
          padding: "12px 16px", marginBottom: 14,
          background: `${T.red}10`, border: `1px solid ${T.red}40`, borderRadius: 6,
        }}>
          <div style={{ fontSize: 10, color: T.red, fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>
            ⚠️ FAILURE MODE · DUPLICATE DELIVERY
          </div>
          <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.55 }}>
            If step 7 fails (offset commit) after step 6 succeeded (consumer applied the write),
            the consumer will reprocess the same message on the next poll — because the broker
            still thinks it wasn't consumed.
          </div>
          <div style={{ fontSize: 10, color: T.green, marginTop: 8, fontWeight: 600 }}>
            ✓ SOLUTION: idempotent consumer keyed on message_id (slide 16 / 17 of the ESB Siemens deck).
            Receive twice, apply once, same outcome. Every CLM write path needs this.
          </div>
        </div>
      )}

      {/* Cross-tab refs */}
      {ifaces.length > 0 && (
        <div style={{
          padding: "12px 16px",
          background: T.bg2, border: `1px solid ${selectedFlow.color}40`,
          borderLeft: `4px solid ${selectedFlow.color}`, borderRadius: 6,
        }}>
          <div style={{ fontSize: 10, color: selectedFlow.color, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            🔗 INTERFACES PARTICIPATING IN THIS FLOW
          </div>
          <EntityRefList kind="interface" ids={ifaces} />
        </div>
      )}
    </div>
  );
}
